"use client"

import { useEffect, useRef, useState } from "react"
import type { RefObject } from "react"
import { useAdFrequencyStore } from "@/lib/ad-frequency-store"
import { markAdShown, isAdCooldownExpired } from "@/lib/ad-cooldown"
import { cn } from "@/lib/utils"

// Moved out of components/booru-detail-modal.tsx (same state, same effects in the same order, same dependencies).
export function useVideoAdOverlays({
  videoEl,
  postId,
  videoRef,
  isMediaVideo,
  activeVideoUrl,
  effectivePremiumUser,
  showVideoPreroll,
  showInterstitial,
  viewingAdSlot,
  isVRMode,
}: {
  /** The mounted <video> (see use-mounted-video.ts); listeners re-attach when it appears. */
  videoEl: HTMLVideoElement | null
  postId: string | number | undefined
  videoRef: RefObject<HTMLVideoElement | null>
  isMediaVideo: boolean
  activeVideoUrl: string | null
  effectivePremiumUser: boolean
  showVideoPreroll: boolean
  showInterstitial: boolean
  viewingAdSlot: number | null
  isVRMode: boolean
}) {
  // Small corner ad shown when a video the user has actually been watching
  // gets paused — see the play/pause listener effect below. hasPlayedOnceRef
  // gates the very first frame (a video that's never played yet getting
  // paused mid-buffer shouldn't trigger this) and resets per-post.
  const [showPauseOverlayAd, setShowPauseOverlayAd] = useState(false)
  const hasPlayedOnceRef = useRef(false)
  // Drives the letterbox/pillarbox gutter-ad dim — see the listener effect
  // below (near the pause-overlay one, same shape). Separate from
  // showPauseOverlayAd: that one fires once on pause and stays up until
  // dismissed/replaced, this one is a live "is video moving right now" flag
  // for as long as the video plays.
  const [isVideoActivelyPlaying, setIsVideoActivelyPlaying] = useState(false)
  // Admin-controlled (Frequency console → letterbox_ads/pillarbox_ads → "Dim
  // while video plays") — was hardcoded on with no way to turn it off.
  // Letterbox and pillarbox get independent toggles in the console (each is
  // its own card), so this reads each one separately rather than sharing a
  // single flag for both bar types.
  const dimLetterboxEnabled = useAdFrequencyStore((s) => s.getDimWhilePlaying("letterbox_ads"))
  const dimPillarboxEnabled = useAdFrequencyStore((s) => s.getDimWhilePlaying("pillarbox_ads"))
  const letterboxFadeClass = cn(
    "transition-opacity duration-700 ease-out",
    isVideoActivelyPlaying && dimLetterboxEnabled ? "opacity-20" : "opacity-100",
  )
  const pillarboxFadeClass = cn(
    "transition-opacity duration-700 ease-out",
    isVideoActivelyPlaying && dimPillarboxEnabled ? "opacity-20" : "opacity-100",
  )

  // Reset the pause-overlay-ad gate per post — a video that's already
  // shown the overlay (or hasn't played yet) shouldn't carry either state
  // into the next post opened.
  useEffect(() => {
    hasPlayedOnceRef.current = false
    setShowPauseOverlayAd(false)
  }, [postId])

  // Pause overlay ad: small corner card when a video the user has actually
  // been watching gets paused. Independent listener from the script-sync
  // one above so this can't interfere with (or be suppressed by)
  // videoSyncSuppressRef, which exists for a different purpose entirely.
  useEffect(() => {
    const video = videoRef.current
    if (!video || !isMediaVideo || effectivePremiumUser) return

    const onPlay = () => {
      hasPlayedOnceRef.current = true
      setShowPauseOverlayAd(false)
    }
    const onPause = () => {
      if (!hasPlayedOnceRef.current) return
      // Don't stack on top of a preroll/interstitial/other overlay already
      // occupying this view.
      if (showVideoPreroll || showInterstitial || viewingAdSlot !== null) return
      // No ads of any kind inside VR mode — a 2D popup floating in the
      // immersive sphere is disorienting, not just visually inconsistent
      // with the flat-mode gutter-ad dimming behavior.
      if (isVRMode) return
      if (!useAdFrequencyStore.getState().isEnabled("pause_overlay_ads")) return
      const cooldown = useAdFrequencyStore.getState().getCooldownMinutes("pause_overlay_ads")
      if (!isAdCooldownExpired("pause_overlay", cooldown)) return
      setShowPauseOverlayAd(true)
      markAdShown("pause_overlay")
    }

    video.addEventListener("play", onPlay)
    video.addEventListener("pause", onPause)
    return () => {
      video.removeEventListener("play", onPlay)
      video.removeEventListener("pause", onPause)
    }
  // showVideoPreroll/showInterstitial/viewingAdSlot MUST be deps, not just
  // read once and closed over: onPause references them to avoid stacking on
  // an overlay that's already showing. Without these in the array, the
  // listener attaches once (effectively at video-open time) and onPause
  // keeps whatever those three were AT THAT MOMENT forever, even after they
  // change — reported live as "pause ads don't work at all". Concretely: the
  // video-preroll effect below very often sets showVideoPreroll=true on the
  // very same open (every Nth video normally, or literally every video in
  // Force Ads test mode). onPause's stale closure then permanently believed
  // a preroll was still showing and bailed out on line 1693, even long after
  // the real showVideoPreroll had gone back to false and the user was just
  // watching normally. Including them re-attaches the listener (cheap — it's
  // a plain addEventListener pair) whenever any of them actually change, so
  // onPause always sees their current value.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMediaVideo, activeVideoUrl, effectivePremiumUser, showVideoPreroll, showInterstitial, viewingAdSlot, videoEl])

  // Dims the letterbox/pillarbox gutter ads while the video is actively
  // playing, fading them back up on pause — so they don't compete for
  // attention with the content the user is actually watching, but stay
  // fully visible (and fully clickable) the rest of the time. A third,
  // independent listener from the two above: this only ever toggles one
  // boolean, so it doesn't need any of their stacking/suppression logic.
  useEffect(() => {
    const video = videoRef.current
    if (!video || !isMediaVideo) { setIsVideoActivelyPlaying(false); return }

    const onPlay = () => setIsVideoActivelyPlaying(true)
    const onPauseOrEnd = () => setIsVideoActivelyPlaying(false)

    video.addEventListener("play", onPlay)
    video.addEventListener("pause", onPauseOrEnd)
    video.addEventListener("ended", onPauseOrEnd)
    // Sync current state on attach, not just future transitions. Most video
    // here autoplays, and autoplay can start (and fire its own "play" event)
    // before this effect's listener exists — reported live as the gutter
    // ads never dimming even on a video that was visibly already playing.
    // Without this, a missed initial event left the flag stuck at its
    // default false for the whole viewing session.
    setIsVideoActivelyPlaying(!video.paused)
    return () => {
      video.removeEventListener("play", onPlay)
      video.removeEventListener("pause", onPauseOrEnd)
      video.removeEventListener("ended", onPauseOrEnd)
    }
  // showVideoPreroll/showInterstitial/viewingAdSlot MUST be deps here too, for the same
  // reason the onPlay/onPause effect above them needs them (see its comment): the real
  // <video ref={videoRef}> element doesn't mount at all while a preroll/interstitial/ad
  // slot is covering it, so on a post that opens straight into one of those (every VR
  // post tested, apparently always ad-gated), this effect's first run finds
  // videoRef.current still null, hits the early return, and never attaches anything.
  // The "sync on attach" line above only fixes the *already-mounted-video autoplayed
  // before this ran* case (its own documented history) — it never covers *the video
  // element didn't exist yet*, since there's nothing to sync from. Without these deps,
  // once the ad clears and the real video mounts, nothing re-runs this effect to attach
  // to it — confirmed live: video.paused was false (genuinely playing, toggled by hand)
  // while isVideoActivelyPlaying stayed stuck false the whole time, so the gutter ads
  // never dimmed for the entire session on that post. Including them re-attaches (and
  // resyncs) whenever the gate actually lifts.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMediaVideo, activeVideoUrl, showVideoPreroll, showInterstitial, viewingAdSlot, videoEl])

  return { showPauseOverlayAd, setShowPauseOverlayAd, letterboxFadeClass, pillarboxFadeClass }
}
